import React, { useState, useEffect, useRef } from "react";
import Loading from "./Loading";
import { useGameTimer } from "../gameTimer";
import { socket } from "../socket";
import { t } from "../i18n";
import { useCountdownTick } from "../sound";

// Culture quiz screen. Like the Blind Test, the pace is set by the server: we
// display the question it sends and compute the remaining time from `startedAt`,
// never deciding on our own to move to the next one. Refreshing the page
// therefore does not reset the countdown.
function Quiz({ gameId }) {
  const [question, setQuestion]   = useState(null);
  const [index, setIndex]         = useState(0);
  const [total, setTotal]         = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [duration, setDuration]   = useState(20000);
  const [answer, setAnswer]       = useState("");
  const [pbAnswers, setPbAnswers] = useState([]);     // scattergories: one answer per category
  const [chronoOrder, setChronoOrder] = useState([]); // current order of the images (chrono format)
  const [now, setNow]             = useState(Date.now());
  const dragIdx = useRef(null);

  useEffect(() => {
    // `quizState` (resume) and `quizQuestion` (new question) carry the same
    // payload: input already recorded server-side is fed back in so a refresh
    // loses nothing.
    const apply = (s) => {
      setIndex(s.index); setTotal(s.total || 0); setQuestion(s.question);
      setStartedAt(s.startedAt); setDuration(s.duration);
      const q = s.question;
      if (q?.format === "petit_bac") {
        setPbAnswers(Array.isArray(s.myAnswer) ? s.myAnswer : (q.categories || []).map(() => ""));
      } else if (q?.format === "chrono") {
        setChronoOrder(Array.isArray(s.myAnswer) && s.myAnswer.length ? s.myAnswer : (q.images || []));
      } else {
        setAnswer(typeof s.myAnswer === "string" ? s.myAnswer : "");
      }
    };
    socket.on("quizState", apply);
    socket.on("quizQuestion", apply);
    socket.emit("quizGetState", { gameId });
    return () => {
      socket.off("quizState", apply);
      socket.off("quizQuestion", apply);
    };
  }, [gameId]);

  useEffect(() => {
    if (!startedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [startedAt]);

  // Input is pushed to the server as it is typed: if time runs out or the page
  // is refreshed, the last answer is already recorded.
  const push = (value) => socket.emit("quizAnswer", { gameId, index, answer: value });

  const setText = (value) => { setAnswer(value); push(value); };
  const setCategory = (i, value) => {
    setPbAnswers(prev => {
      const next = [...prev];
      next[i] = value;
      push(next);
      return next;
    });
  };
  const moveImage = (from, to) => {
    setChronoOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      push(next);
      return next;
    });
  };

  const remaining = startedAt
    ? Math.max(0, Math.ceil((duration - (now - startedAt)) / 1000))
    : 0;
  const over = remaining === 0;

  useCountdownTick(remaining, { active: !!startedAt });

  // Le décompte part dans la barre de navigation, au centre, comme dans tous
  // les autres modes.
  useGameTimer(startedAt ? { remaining, duration } : null);

  // missing `startedAt` = the question has not started server-side yet
  if (!question || !startedAt) return <Loading full />;
  const isChrono = question.format === "chrono";
  const isPetitBac = question.format === "petit_bac";

  return (
    <div class="Quiz">
      {/* Enveloppe neutre par défaut (display: contents) : elle ne sert qu'aux
          habillages qui posent la question sur une plaque. */}
      <div class="quiz-card">
      <h2 class="quiz-question-number">{total ? t("quiz.questionOf", { n: index + 1, total }) : t("quiz.questionNumber", { n: index + 1 })}</h2>
      {question.media_url && (
        /\.(jpe?g|png|gif|webp)$/i.test(question.media_url)
          ? <img src={question.media_url} alt="" className="quiz-media" />
          : /\.(mp4|webm)$/i.test(question.media_url)
          ? <video controls src={question.media_url} className="quiz-media" />
          : <audio controls src={question.media_url} />
      )}
      <div class="question">
        <h3>{question.question}</h3>
        <p>{isPetitBac ? t("quiz.pointsPerCategory") : t("quiz.pointsValue", { count: question.difficulty })}</p>
      </div>
      {isPetitBac ? (
        <div class="pb-board">
          {(question.categories || []).map((cat, i) => (
            <label key={i} class="pb-row">
              <span class="pb-label">{cat}</span>
              <input
                value={pbAnswers[i] || ""}
                onChange={e => setCategory(i, e.target.value)}
                placeholder="…"
                disabled={over}
              />
            </label>
          ))}
        </div>
      ) : isChrono ? (
        <div class="chrono-board">
          <p class="chrono-hint">{t("quiz.chronoHint", { count: chronoOrder.length })}</p>
          <div class="chrono-list">
            {chronoOrder.map((url, i) => (
              <div key={url} class="chrono-item" draggable={!over}
                onDragStart={() => { dragIdx.current = i; }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const from = dragIdx.current;
                  if (from === null || from === i) return;
                  moveImage(from, i);
                  dragIdx.current = null;
                }}>
                <span class="chrono-item-num">{i + 1}</span>
                <img src={url} alt={t("quiz.position", { n: i + 1 })} draggable={false} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div class="answer-section">
          <input
            value={answer}
            onChange={e => setText(e.target.value)}
            placeholder={t("quiz.yourAnswer")}
            disabled={over}
          />
        </div>
      )}
      </div>
    </div>
  );
}

export default Quiz;
